import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

const TrackRow = styled.div`
    position: relative;
    height: 26px;
    background: #0e0e1a;
    border-bottom: 1px solid #2a2a3a;
    overflow: hidden;
`;

const CueBlock = styled.div`
    position: absolute;
    top: 4px;
    height: 18px;
    border-radius: 2px;
    background: ${({ $dragging }) =>
        $dragging ? 'rgba(38,166,154,0.85)' : 'rgba(38,166,154,0.4)'};
    border: 1px solid ${({ $dragging }) =>
        $dragging ? '#80cbc4' : '#26a69a'};
    overflow: hidden;
    cursor: ${({ $dragging }) => ($dragging ? 'grabbing' : 'grab')};
    box-sizing: border-box;
    display: flex;
    align-items: center;
    padding: 0 4px;
    user-select: none;
    touch-action: none;
    pointer-events: auto;
`;

const CueText = styled.span`
    font-size: 9px;
    color: #b2dfdb;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    user-select: none;
    pointer-events: none;
`;

const RippleHint = styled.div`
    position: absolute;
    top: 0;
    right: 4px;
    font-size: 8px;
    color: #26a69a;
    opacity: 0.7;
    pointer-events: none;
    line-height: 26px;
`;

function outputToOriginalTime(t, sortedCuts) {
    let pos = t;
    let accumulated = 0;
    for (const cut of sortedCuts) {
        const cutDuration = cut.endTime - cut.startTime;
        const adjustedStart = cut.startTime - accumulated;
        if (pos >= adjustedStart) {
            pos += cutDuration;
            accumulated += cutDuration;
        } else {
            break;
        }
    }
    return pos;
}

export default function SubtitleTrackBar({
    cues,
    zoomLevel,
    scrollLeft,
    language,
    outputDuration,
    cuts,
    showDeleted,
    leftOffset = 0,
    onCueDrop,
}) {
    const sortedCuts = useMemo(
        () => (cuts ? [...cuts].sort((a, b) => a.startTime - b.startTime) : []),
        [cuts],
    );

    // { cueId, deltaSeconds, ripple } — live preview only, committed on mouseup
    const [dragState, setDragState] = useState(null);

    // Stable refs so document listeners always see the current values
    const zoomRef = useRef(zoomLevel);
    zoomRef.current = zoomLevel;
    const onCueDropRef = useRef(onCueDrop);
    onCueDropRef.current = onCueDrop;

    const startDrag = (e, cueId) => {
        if (!onCueDropRef.current) return;
        e.preventDefault();
        e.stopPropagation();

        const ripple = !e.altKey;
        const startX = e.clientX;

        setDragState({ cueId, deltaSeconds: 0, ripple });

        const onMove = (ev) => {
            const delta = (ev.clientX - startX) / zoomRef.current;
            setDragState({ cueId, deltaSeconds: delta, ripple });
        };

        const onUp = (ev) => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            const delta = (ev.clientX - startX) / zoomRef.current;
            setDragState(null);
            if (Math.abs(delta) > 0.01) {
                onCueDropRef.current(cueId, delta, ripple);
            }
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    if (!cues || cues.length === 0) return (
        <TrackRow>
            <span style={{ position: 'absolute', left: 6, top: 7, fontSize: 12, color: '#555' }}>
                No subtitles
            </span>
        </TrackRow>
    );

    const draggedCue = dragState ? cues.find((c) => c.id === dragState.cueId) : null;

    return (
        <TrackRow>
            {showDeleted && sortedCuts.map((cut) => {
                const left = cut.startTime * zoomLevel + leftOffset;
                const width = (cut.endTime - cut.startTime) * zoomLevel;
                if (left + width < scrollLeft || left > scrollLeft + window.innerWidth) return null;
                return (
                    <div
                        key={`del-${cut.id}`}
                        style={{
                            position: 'absolute',
                            top: 4,
                            height: 22,
                            left: `${left}px`,
                            width: `${Math.max(2, width)}px`,
                            background: 'rgba(183, 28, 28, 0.45)',
                            border: '2px solid #b71c1c',
                            borderRadius: 2,
                            boxSizing: 'border-box',
                        }}
                    />
                );
            })}

            {cues.map((cue) => {
                let startPos = cue.startTime;
                let endPos = cue.endTime;
                if (showDeleted && sortedCuts.length > 0) {
                    startPos = outputToOriginalTime(cue.startTime, sortedCuts);
                    endPos = outputToOriginalTime(cue.endTime, sortedCuts);
                }

                const isDragging = dragState?.cueId === cue.id;
                const isRippled = !isDragging &&
                    dragState?.ripple &&
                    draggedCue &&
                    cue.startTime > draggedCue.startTime;
                const delta = (isDragging || isRippled) ? (dragState?.deltaSeconds ?? 0) : 0;

                const left = (startPos + delta) * zoomLevel + leftOffset;
                const width = (endPos - startPos) * zoomLevel;
                if (left + width < scrollLeft || left > scrollLeft + window.innerWidth) return null;

                const active = isDragging || isRippled;

                return (
                    <CueBlock
                        key={cue.id}
                        $dragging={active}
                        title={active ? '' : cue.text}
                        style={{ left: `${left}px`, width: `${Math.max(8, width)}px` }}
                        onMouseDown={(e) => startDrag(e, cue.id)}
                    >
                        {width > 24 && <CueText>{cue.text}</CueText>}
                    </CueBlock>
                );
            })}

            {dragState && (
                <RippleHint>
                    {dragState.ripple ? '⇉ ripple' : '→ single'} · Alt=single
                </RippleHint>
            )}
        </TrackRow>
    );
}
